'use client';

import {
  DETAIL_ANIMAL_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { SESSION_STATUS } from '@/constants/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { debounce } from 'lodash';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { LogicalOperator } from '@/components/publicSearch/constants';
import {
  useGetAnimalEbvCountLazyQuery,
  useGetLotAnimalEbvCountLazyQuery,
  useGetMembersCountLazyQuery,
} from '@/publicSearch/graphql/queries.generated';
import { useMemberAssociation } from '@/publicSearch/graphql/memberAssociation/hooks';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import useGridStore from '@/publicSearch/store/useGridStore';
import type {
  FilterGroup,
  FilterInput,
} from '@/components/publicSearch/constants';
import { isFilterInputIncompleteForQuery } from '@/components/publicSearch/search/searchFilterEbvExpansion';
import {
  buildAnimalFilterGroups,
  buildAnimalFilterGroupsFromGroups,
} from './animal/buildAnimalFilterGroups';
import { buildLotAnimalFilterGroups } from './sale-catalogue/buildLotAnimalFilterGroups';
import {
  buildMemberFilterGroups,
  buildMemberFilterGroupsFromGroups,
} from './buildMemberFilterGroups';
import { isUnmappedI6LookupError } from '@/components/publicSearch/search/expandI6LookupFilters';
import { QUICK_SEARCH_DEBOUNCE_MS } from '@/components/publicSearch/search/quickSearchConfig';
import { useSaleIdParam } from './useSaleIdParam';

interface UsePreviewCountParams {
  filters: FilterInput[];
  searchType: string;
  commonFilterOperator: string;
  enabled: boolean;
  filterGroups?: FilterGroup[];
  groupsOperator?: string;
}

export function usePreviewCount({
  filters,
  searchType,
  commonFilterOperator,
  enabled,
  filterGroups: groupedFilters,
  groupsOperator,
}: UsePreviewCountParams) {
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const societySettings = usePublicSearchContext(
    (context) => context.society.settings,
  );
  const saleId = useSaleIdParam();
  const { status } = useSession();
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;
  const { data: memberAssocData } = useMemberAssociation(!isAuthenticated);
  const { myAnimals, animalsOnSaleOnly } = useGridStore();

  const myAnimalsMemberIds = useMemo(
    () => memberAssocData?.memberAssociation ?? [],
    [memberAssocData?.memberAssociation],
  );

  const [count, setCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState<boolean>(false);

  const [getAnimalCount] = useGetAnimalEbvCountLazyQuery({
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  });
  const [getLotAnimalCount] = useGetLotAnimalEbvCountLazyQuery({
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  });
  const [getMembersCount] = useGetMembersCountLazyQuery({
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  });

  const isDetailAnimalsType = DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType);
  const isAnimalType = searchType === SEARCH_TYPE.ANIMAL;
  const animalsOnSaleOnlyForQuery = isAnimalType && animalsOnSaleOnly;
  const isMembersType = searchType === SEARCH_TYPE.MEMBERS;

  const hasValidFilters = useMemo(() => {
    if (filters.length === 0) return true;
    return !filters.some((f) => {
      if (!f.field || f.field.startsWith('__lookup__')) return false;
      return isFilterInputIncompleteForQuery(f);
    });
  }, [filters]);

  const fetchCount = useCallback(() => {
    if (!enabled || !orgId || !hasValidFilters) return;

    if (isDetailAnimalsType) {
      if (!saleId) return;
      let filterGroups;
      try {
        filterGroups = buildLotAnimalFilterGroups({
          saleId,
          filters,
          commonFilterOperator: commonFilterOperator as LogicalOperator,
          settings: societySettings,
        });
      } catch (error) {
        // ILRX-4434: skip preview — never count with dropped Select-if / no saleId.
        if (isUnmappedI6LookupError(error)) return;
        throw error;
      }

      setCountLoading(true);
      getLotAnimalCount({
        variables: {
          requestInput: {
            pagination: { currentPage: 1, pageSize: 1 },
            filterGroups,
          },
        },
        context: { headers: setDefaultHeader(orgId) },
      })
        .then((result) => {
          setCount(result.data?.lotAnimalEbvs?.totalCount ?? null);
          setCountLoading(false);
        })
        .catch(() => {
          setCountLoading(false);
        });
      return;
    }

    if (isAnimalType) {
      let filterGroups;
      try {
        filterGroups =
          groupedFilters && groupedFilters.length > 0
            ? buildAnimalFilterGroupsFromGroups(
                groupedFilters,
                groupsOperator || LogicalOperator.or,
                myAnimals,
                myAnimalsMemberIds,
                orgId,
                animalsOnSaleOnlyForQuery,
                societySettings,
              )
            : buildAnimalFilterGroups(
                filters,
                commonFilterOperator,
                myAnimals,
                myAnimalsMemberIds,
                orgId,
                animalsOnSaleOnlyForQuery,
                societySettings,
              );
      } catch (error) {
        if (isUnmappedI6LookupError(error)) return;
        throw error;
      }
      if (!filterGroups) return;

      setCountLoading(true);
      getAnimalCount({
        variables: {
          requestInput: {
            pagination: { currentPage: 1, pageSize: 1 },
            filterGroups,
            includeTotalCount: true,
          },
        },
        context: { headers: setDefaultHeader(orgId) },
      })
        .then((result) => {
          setCount(result.data?.animalEbvs?.totalCount ?? null);
          setCountLoading(false);
        })
        .catch(() => {
          setCountLoading(false);
        });
    } else if (isMembersType) {
      // Pass the un-stripped `filters` (not `submittableFilters`) so
      // member lookup-pill markers (`__lookup__ms_*`) are still present
      // for buildMemberFilterGroups to expand into the underlying
      // `i6LookupString`-derived filter(s) via society.settings. The basic
      // search also keeps the real filter alongside the marker; dedupe in
      // the build pipeline collapses the duplicate.
      // Prefer grouped Advanced Filter state when present (parity with animals).
      let filterGroups;
      try {
        filterGroups =
          groupedFilters && groupedFilters.length > 0
            ? buildMemberFilterGroupsFromGroups(
                groupedFilters,
                groupsOperator || LogicalOperator.or,
                societySettings,
              )
            : buildMemberFilterGroups(
                filters,
                commonFilterOperator,
                societySettings,
              );
      } catch (error) {
        if (isUnmappedI6LookupError(error)) return;
        throw error;
      }

      setCountLoading(true);
      getMembersCount({
        variables: {
          requestInput: {
            pagination: { currentPage: 1, pageSize: 1 },
            filterGroups,
          },
        },
        context: { headers: setDefaultHeader(orgId) },
      })
        .then((result) => {
          setCount(result.data?.members?.totalCount ?? null);
          setCountLoading(false);
        })
        .catch(() => {
          setCountLoading(false);
        });
    }
  }, [
    enabled,
    orgId,
    hasValidFilters,
    filters,
    isAnimalType,
    isDetailAnimalsType,
    isMembersType,
    commonFilterOperator,
    myAnimals,
    animalsOnSaleOnlyForQuery,
    myAnimalsMemberIds,
    getAnimalCount,
    getLotAnimalCount,
    getMembersCount,
    groupedFilters,
    groupsOperator,
    societySettings,
    saleId,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchCount = useCallback(
    debounce(() => {
      fetchCountRef.current();
    }, QUICK_SEARCH_DEBOUNCE_MS),
    [],
  );

  const fetchCountRef = useRef<() => void>(fetchCount);
  fetchCountRef.current = fetchCount;

  useEffect(() => {
    if (!enabled) {
      setCount(null);
      return;
    }

    debouncedFetchCount();

    return () => {
      debouncedFetchCount.cancel();
    };
  }, [
    enabled,
    filters,
    commonFilterOperator,
    myAnimals,
    animalsOnSaleOnlyForQuery,
    searchType,
    saleId,
    debouncedFetchCount,
    groupedFilters,
    groupsOperator,
  ]);

  return { count, loading: countLoading };
}
